import { DashboardLayout } from 'components/layout/DashboardLayout';
import { GeneralTable } from 'components/tables/GeneralTable';
import { useCustomSelector } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useTableColumns } from './hooks/useTableColumns';

import "assets/scss/billingPortal/modules/reporting/activationLog.scss";
import { getTotalCountFromHeader, isObjectEmpty, isArrayEmpty, isNullUndefined, func_datetime } from 'functions';
import useReporting from 'dataflow/billingPortal/actions/useReporting.actions';
import { MultiFilters } from 'components/filters/MultiFilters';
import { Dropdown, Button } from 'components';
import { DateRangePicker } from 'components/form/DateRangePicker';
import { SubmenuTooltip } from 'components/informational/SubmenuTooltip';
import { MoreButton } from 'components/buttons/MoreButton';
import ic_export from 'assets/images/ic_export.svg';
import useExportData from './hooks/useExportData';
import moment from 'moment';

export default function ActivationLog() {
    const [pageTitle] = useState("Product Code");
    const [productCode, setProductCode] = useState("");
    const [isTableVisible, setIsTableVisible] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [tempCompanyId, setTempCompanyId] = useState("");
    const [selectedLocationNames, setSelectedLocationNames] = useState([]);
    const [isDatepickerResetRequired, setIsDatepickerResetRequired] = useState(false);

    const {
        getActivationLogReport_requesting, activationLogReportList, activationLogReportListHeaders,
    } = useCustomSelector("reporting");

    const {
        userPermissions
    } = useCustomSelector("userManagementReducers");

    const {
        getActivationLogReport
    } = useReporting();

    const columns = useTableColumns(productCode, companyId);
    const exportData = useExportData();

    // Reset dates when product code changes or table closes
    useEffect(() => {
        setTempStartDate(null);
        setTempEndDate(null);
        setTempCompanyId("");
        setSelectedLocationNames([]);
        setIsDatepickerResetRequired(true);
    }, [productCode, isTableVisible]);

    // Reset the reset flag
    useEffect(() => {
        if (isDatepickerResetRequired) {
            setIsDatepickerResetRequired(false);
        }
    }, [isDatepickerResetRequired]);

    // Product code options
    const productCodeOptions = [
        { label: "XLO001", value: "XLO001" },
        { label: "Loyalty Lite", value: "Loyalty Lite" },
    ];

    function searchActivationLog() {
        setIsTableVisible(true);
    }

    function handleExportClick() {
        exportData(activationLogReportList, productCode, companyId);
    }

    function getLocationNameFromRow(row) {
        const rawValue =
            row?.LocationName ??
            row?.locationName ??
            row?.ActivationLogInfo?.LocationName ??
            row?.ActivationLogInfo?.locationName ??
            row?.["ActivationLogInfo.LocationName"] ??
            row?.["ActivationLogInfo.locationName"];

        if (Array.isArray(rawValue)) {
            return rawValue.filter(Boolean).join(", ").trim();
        }

        if (typeof rawValue === "string") {
            return rawValue.trim();
        }

        return "";
    }

    const locationNameOptions = useMemo(() => {
        if (!Array.isArray(activationLogReportList)) {
            return [];
        }

        const counts = new Map();
        activationLogReportList.forEach((row) => {
            const locationName = getLocationNameFromRow(row);
            if (!locationName) {
                return;
            }

            counts.set(locationName, (counts.get(locationName) || 0) + 1);
        });

        return Array.from(counts.entries())
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [activationLogReportList]);

    const tableData = useMemo(() => {
        const source = userPermissions?.Internal_ActivationLog_View && Array.isArray(activationLogReportList)
            ? activationLogReportList
            : [];

        if (productCode !== "XLO001" || selectedLocationNames.length === 0) {
            return source;
        }

        const selectedSet = new Set(selectedLocationNames);
        return source.filter((row) => selectedSet.has(getLocationNameFromRow(row)));
    }, [activationLogReportList, productCode, selectedLocationNames, userPermissions?.Internal_ActivationLog_View]);

    const isAllLocationNamesSelected =
        locationNameOptions.length > 0 &&
        selectedLocationNames.length === locationNameOptions.length;

    function toggleLocationName(name) {
        setSelectedLocationNames((previous) => {
            if (previous.includes(name)) {
                return previous.filter((value) => value !== name);
            }
            return [...previous, name];
        });
    }

    function toggleAllLocationNames() {
        if (isAllLocationNamesSelected) {
            setSelectedLocationNames([]);
            return;
        }

        setSelectedLocationNames(locationNameOptions.map((option) => option.name));
    }

    let mainDisplay = (
        <div className='activation-log-report__content'>
            <div className={'activation-log-report__content-body'}>
                <div className={"row"}>
                    <div className='col-12 col-lg-5 pb-4'>
                        <Dropdown
                            options={productCodeOptions}
                            selectedValue_default={productCode}
                            onChange={(value, label) => {
                                setProductCode(value);
                            }}
                            onEnterKeyPressed={searchActivationLog}
                            label={pageTitle}
                            isLabelLegend
                        />
                    </div>
                    <div className='col-12 col-lg-2 pt-0 pt-lg-4'>
                        <Button
                            className={"grab-button__input float-right float-lg-none"}
                            isLoading={getActivationLogReport_requesting}
                            isDisabled={getActivationLogReport_requesting || !productCode}
                            handleOnClick={searchActivationLog}
                        >
                            Search
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );

    let tableDisplay = (
        <GeneralTable
            className={"activation-log-report__table"}
            columns={columns}
            data={tableData}
            pageSize={1000}

            getTableData={getActivationLogReport}
            defaultFilter={{ productCode: productCode }}
            persistentFilterOptions={{ productCode: productCode }}

            outerTableHeader={(outerTableHeader_props) => {
                const { 
                    filterOptions, setFilterOptions
                } = outerTableHeader_props;

                return (
                    <div className={"table__headers"}>
                        {/* Top Section */}
                        <div className={"headers__top-section d-flex justify-content-between align-items-center"}>
                            {/* Title */}
                            <div className={"headers__title tableHeaderTitle "}>
                                {pageTitle} - {productCode}
                            </div>

                            <div className={"header__action d-flex align-items-center"}>
                                {/* Export Action */}
                                <SubmenuTooltip
                                    className={"action__more-actions-wrapper ml-2"}
                                    menuItems={[
                                        {
                                            icon: ic_export,
                                            label: "Export",
                                            isDisabled: isArrayEmpty(activationLogReportList) || isNullUndefined(activationLogReportList),
                                            onClick: handleExportClick,
                                        }
                                    ]}
                                >
                                    <MoreButton
                                        className={"more-actions-wrapper__more-actions"}
                                        iconClassName={"more-actions__icon"}
                                    />
                                </SubmenuTooltip>
                            </div>
                        </div>

                        {/* Bottom Section */}
                        <div className={"headers__bottom-section d-flex flex-column"}>
                            <div className="d-flex align-items-end">
                                <div className="flex-grow-1">
                                    <label className="form-label">Company ID</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter Company ID"
                                        value={tempCompanyId}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                setTempCompanyId(value);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            
                            {productCode === "Loyalty Lite" && (
                                <div className={"mt-3 d-flex align-items-end"}>
                                    <DateRangePicker
                                        label="Date"
                                        isLabelRequired
                                        startDate={tempStartDate}
                                        endDate={tempEndDate}
                                        handleOnChange_start={setTempStartDate}
                                        handleOnChange_end={setTempEndDate}
                                        placeholder_start="dd/mm/yyyy"
                                        placeholder_end="dd/mm/yyyy"
                                        isResetRequired={isDatepickerResetRequired}
                                    />
                                    <Button
                                        className={"ml-2"}
                                        handleOnClick={() => {
                                            console.log('Filter clicked - dates:', { tempStartDate, tempEndDate });
                                            
                                            // If dates are empty, trigger reset
                                            if (!tempStartDate && !tempEndDate) {
                                                setIsDatepickerResetRequired(true);
                                            }
                                            
                                            let newFilters = {
                                                ...filterOptions,
                                                productCode: productCode
                                            };
                                            
                                            // Only add filters if they have values
                                            if (tempCompanyId) {
                                                newFilters.companyId = tempCompanyId;
                                            }
                                            if (tempStartDate && moment(tempStartDate).isValid()) {
                                                newFilters.startDate = moment(tempStartDate).format("YYYY-MM-DD");
                                                console.log('Adding startDate:', newFilters.startDate);
                                            }
                                            if (tempEndDate && moment(tempEndDate).isValid()) {
                                                newFilters.toDate = moment(tempEndDate).format("YYYY-MM-DD");
                                                console.log('Adding toDate:', newFilters.toDate);
                                            }
                                            
                                            console.log('Final filters:', newFilters);
                                            
                                            setCompanyId(tempCompanyId || null);
                                            setFilterOptions(newFilters);
                                        }}
                                    >
                                        Filter
                                    </Button>
                                </div>
                            )}
                            
                            {productCode === "XLO001" && (
                                <div className={"mt-3"}>
                                    <div className={"d-flex align-items-end"}>
                                        <DateRangePicker
                                            label="Date"
                                            isLabelRequired
                                            startDate={tempStartDate}
                                            endDate={tempEndDate}
                                            handleOnChange_start={setTempStartDate}
                                            handleOnChange_end={setTempEndDate}
                                            placeholder_start="dd/mm/yyyy"
                                            placeholder_end="dd/mm/yyyy"
                                            isResetRequired={isDatepickerResetRequired}
                                        />
                                        <Button
                                            className={"ml-2"}
                                            handleOnClick={() => {
                                                console.log('Filter clicked - dates:', { tempStartDate, tempEndDate });
                                                
                                                // If dates are empty, trigger reset
                                                if (!tempStartDate && !tempEndDate) {
                                                    setIsDatepickerResetRequired(true);
                                                }
                                                
                                                let newFilters = {
                                                    ...filterOptions,
                                                    productCode: productCode
                                                };
                                                
                                                // Only add filters if they have values
                                                if (tempCompanyId) {
                                                    newFilters.companyId = tempCompanyId;
                                                }
                                                if (tempStartDate && moment(tempStartDate).isValid()) {
                                                    newFilters.startDate = moment(tempStartDate).format("YYYY-MM-DD");
                                                    console.log('Adding startDate:', newFilters.startDate);
                                                }
                                                if (tempEndDate && moment(tempEndDate).isValid()) {
                                                    newFilters.toDate = moment(tempEndDate).format("YYYY-MM-DD");
                                                    console.log('Adding toDate:', newFilters.toDate);
                                                }
                                                
                                                console.log('Final filters:', newFilters);
                                                
                                                setCompanyId(tempCompanyId || null);
                                                setFilterOptions(newFilters);
                                            }}
                                        >
                                            Filter
                                        </Button>
                                    </div>

                                    {companyId && locationNameOptions.length > 0 && (
                                        <div className="mt-3">
                                            <label className="form-label mb-1">Location Name</label>
                                            <details className="border rounded p-2 bg-white">
                                                <summary style={{ cursor: "pointer" }}>
                                                    {selectedLocationNames.length > 0
                                                        ? `${selectedLocationNames.length} selected`
                                                        : "Filter by value"}
                                                </summary>

                                                <div className="mt-2">
                                                    <label className="d-flex align-items-center mb-2" style={{ gap: "8px" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllLocationNamesSelected}
                                                            onChange={toggleAllLocationNames}
                                                        />
                                                        <span>{`Select All (${locationNameOptions.length})`}</span>
                                                    </label>

                                                    <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                                                        {locationNameOptions.map((option) => (
                                                            <label
                                                                key={option.name}
                                                                className="d-flex align-items-center mb-2"
                                                                style={{ gap: "8px" }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedLocationNames.includes(option.name)}
                                                                    onChange={() => toggleLocationName(option.name)}
                                                                />
                                                                <span>{`${option.name} (${option.count})`}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }}
            
            isTableDataLoading={getActivationLogReport_requesting}

            isUsingPagination
            totalRecords={
                productCode === "XLO001" && selectedLocationNames.length > 0
                    ? tableData.length
                    : getTotalCountFromHeader(activationLogReportListHeaders)
            }

            isColumnResizeable 
            isAllColumnWidthEqual
            isTableOddRowsDarken
        />
    );

    return (
        <DashboardLayout
            headerTitle={"Activation Log"}
            bodyClassName={"activation-log-report"}
            isHeaderCloseIconVisible={isTableVisible}
            updateParentState_visiblity={isTableVisible ? setIsTableVisible : null}
            body={isTableVisible ? tableDisplay : mainDisplay}
        />
    );
}